import React, { useState, useEffect } from 'react'
import { Grid, Typography } from '@mui/material'
import { ColStack } from 'components/atoms/container/InCardStack'
import {
  AssessmentLikertScale,
  AssessmentProgressBar,
  AssessmentNAButton
} from 'components/molecules/assessments/AssessmentMolecules'
import { useTranslation } from 'react-i18next'
import {
  CONSTANTS_OPTIONS,
  USER_SURVEYS_URL,
  USER_SURVEY_STATUSES
} from 'services/constants'
import { getConstant } from 'services/constantServices'
import OverlayLoading from 'components/atoms/OverlayLoading'
import UserSurveyLanding from 'components/molecules/assessments/UserSurveyLanding'
import { getUserSurveys } from 'services/userServices'
import { useNavigate } from 'react-router-dom'
import { MY_JOURNEY } from 'routes/constants'
import { patchRequest } from 'utils/api'
import NotificationSnackBar from 'components/molecules/notificationOverlay/NotificationSnackBar'
import NavigateButtons from 'components/molecules/onboarding/NavigateButtons'
import { AssessmentCompletePopup } from 'components/molecules/assessments/AssessmentMolecules'

const UserSurveyCard = ({
  fromOnboarding = false,
  submittedCallback,
  pendingSurvey
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [landing, setLanding] = useState(true)
  const [questions, setQuestions] = useState([])
  const [answerOptions, setAnswerOptions] = useState([])
  const [answers, setAnswers] = useState([])
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [survey, setSurvey] = useState(pendingSurvey)
  const [loading, setLoading] = useState(true)
  const [congratsDialogOpen, setCongratsDialogOpen] = useState(false)
  const [error, setError] = useState(false)
  const [naOption, setNAOption] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      const optionsConstants = await getConstant(
        CONSTANTS_OPTIONS.EVALUATION_OPTIONS
      )
      setAnswerOptions(optionsConstants)
      setNAOption(optionsConstants.find(o => o.value === 0))

      const questionConstants = await getConstant(
        CONSTANTS_OPTIONS.USER_SURVEY_QUESTIONS
      )
      setQuestions(questionConstants)
      if (!pendingSurvey) {
        const res = await getUserSurveys()
        const pendingSurvey = res?.find(
          s => s.status === USER_SURVEY_STATUSES.PENDING
        )
        if (!pendingSurvey) {
          navigate(MY_JOURNEY)
        }
        setSurvey(pendingSurvey)
      }
      setLoading(false)
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (questions.length) {
      setAnswers(Array(questions.length).fill(null))
    }
  }, [questions])

  let myTimeout = null
  const timeout = 500
  const updateAnswer = (idx, choice, moveToNext = true) => {
    const newAnswers = [...answers]
    newAnswers[idx] = choice
    setAnswers(newAnswers)
    myTimeout = setTimeout(() => {
      if (moveToNext) {
        handleNext()
      }
    }, timeout)
    return () => clearTimeout(myTimeout)
  }

  const answeredPercent = () =>
    questions.length
      ? answers.filter(x => x !== null).length / questions.length
      : 0

  const submitAnswer = async () => {
    const requestBody = {
      scores: answers.map(a => parseInt(a)),
      status: USER_SURVEY_STATUSES.COMPLETED
    }
    const res = await patchRequest(
      `${USER_SURVEYS_URL}/${survey.id}`,
      requestBody
    )
    if (res.status === 200) {
      if (fromOnboarding) {
        submittedCallback(res.data.data)
      } else {
        setCongratsDialogOpen(true)
      }
    } else {
      setError(true)
    }
    return
  }

  const handleNext = (submit = false) => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1)
    } else if (submit) {
      submitAnswer()
    }
  }

  const checkNextDisabled = () => {
    if (currentQuestion < questions.length - 1) {
      return (
        answers[currentQuestion] === null ||
        answers[currentQuestion] === undefined
      )
    }
    return answeredPercent() < 1
  }

  const currentNASelected = () => answers[currentQuestion] === naOption?.value

  const handleNAClick = () => {
    const updatedValue = currentNASelected() ? null : naOption.value
    const moveToNext =
      currentQuestion < questions.length - 1 &&
      !currentNASelected() &&
      answers[currentQuestion + 1] === null
    updateAnswer(currentQuestion, updatedValue, moveToNext)
  }

  const handleComplete = () => {
    setCongratsDialogOpen(false)
    navigate(MY_JOURNEY)
  }

  return (
    <>
      {!landing ? (
        <>
          <ColStack spacing={16}>
            <AssessmentProgressBar
              percentage={answeredPercent()}
              m={currentQuestion + 1}
              n={questions.length}
            />

            {questions.map(
              (q, index) =>
                index === currentQuestion && (
                  <ColStack
                    spacing={16}
                    key={`insight-survey-question-${q.id}`}>
                    <Typography variant='h2'>{q?.question}</Typography>
                    <AssessmentLikertScale
                      id={index + 1}
                      onChange={v => updateAnswer(index, v)}
                      initialValue={answers[index] || -1}
                      options={answerOptions.filter(o => o.value > 0)}
                    />
                  </ColStack>
                )
            )}
            <AssessmentNAButton
              selected={currentNASelected()}
              handleNAClick={handleNAClick}
            />
          </ColStack>

          <Grid position='sticky'>
            <NavigateButtons
              showBack={currentQuestion > 0}
              onBack={() => setCurrentQuestion(currentQuestion - 1)}
              onNext={() => handleNext(true)}
              nextLabel={
                currentQuestion < questions.length - 1
                  ? null
                  : fromOnboarding
                    ? t('userInsightSurvey.completeOnboarding')
                    : t('assessments.submit')
              }
              nextProps={{
                disabled: checkNextDisabled(),
                'data-testid':
                  currentQuestion < questions.length - 1
                    ? 'insight-next-button'
                    : 'submit-button'
              }}
              backProps={{
                'data-testid': 'insight-back-button'
              }}
            />
          </Grid>

          <AssessmentCompletePopup
            congratsDialogOpen={congratsDialogOpen}
            handleComplete={handleComplete}
            forInsightSurvey={true}
          />
        </>
      ) : (
        <UserSurveyLanding
          handleBegin={() => setLanding(false)}
          fromOnboarding={fromOnboarding}
        />
      )}

      {loading && <OverlayLoading open={loading} />}
      <NotificationSnackBar
        error={error}
        open={error}
        setOpen={setError}
        autoHideDuration={5000}
      />
    </>
  )
}

export default UserSurveyCard
